import type { CellContext } from '@tanstack/vue-table';
import { format } from 'date-fns';
import { h } from 'vue';
import { useI18n } from 'vue-i18n';

import { RouterLink } from 'vue-router';
import { FieldType } from '../types';
import BadgeFieldRenderer from './components/BadgeFieldRenderer.vue';
import EnumFieldRenderer from './components/EnumFieldRenderer.vue';

export const useTableRenderers = () => {
  const { t } = useI18n();

  const DateFieldRenderer = (info: CellContext<any, any>) => {
    if (info.getValue() === undefined || info.getValue() === null) {
      return '-';
    }
    const date = new Date(info.getValue<string>());
    return format(date, 'dd.MM.yyyy');
  };

  const TextFieldRenderer = (info: CellContext<any, any>) => {
    return info.getValue() || '-';
  };

  const BooleanFieldRenderer = (info: CellContext<any, any>) => {
    return info.getValue() ? t('common.titles.yes') : t('common.titles.no');
  };

  const MoneyFieldRenderer = (info: CellContext<any, any>) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(info.getValue() || 0);
  };

  const NumberFieldRenderer = (info: CellContext<any, any>) => {
    return info.getValue() || '-';
  };

  const GetFieldRenderer = (
    fieldType: FieldType,
    config?: {
      enumOptions?: {
        label: string;
        value: string;
        class?: string;
      }[];
      link?: {
        name: string;
        key: string;
        dataKey?: string; // Optional: use different field from row data for the param value
      };
      href?: string;
    }
  ) => {
    switch (fieldType) {
      case FieldType.DATE:
        return DateFieldRenderer;
      case FieldType.TEXT:
        return TextFieldRenderer;
      case FieldType.BOOLEAN:
        return BooleanFieldRenderer;
      case FieldType.MONEY:
        return MoneyFieldRenderer;
      case FieldType.NUMBER:
        return NumberFieldRenderer;
      case FieldType.ENUM:
        return (info: CellContext<any, any>) =>
          h(EnumFieldRenderer, {
            value: info.getValue(),
            key: `enum-${info.row.original.id}-${info.getValue()}`,
            enumOptions: config?.enumOptions || [],
          });
      case FieldType.BADGE:
        return (info: CellContext<any, any>) =>
          h(BadgeFieldRenderer, {
            value: info.getValue(),
            key: `badge-${info.row.original.id}-${info.getValue()}`,
            enumOptions: config?.enumOptions || [],
          });
      case FieldType.LINK_INTERNAL:
        return (info: CellContext<any, any>) => {
          if (!config?.link) {
            return info.getValue();
          }
          if (!info.getValue()) {
            return '-';
          }
          const dataKey = config.link.dataKey || config.link.key;
          const paramValue = info.row.original[dataKey];
          if (!paramValue) {
            return info.getValue() || '-';
          }
          return h(
            RouterLink,
            {
              class:
                'tw-underline tw-text-secondary-500 hover:tw-text-primary-600 tw-cursor-pointer',
              to: {
                name: config.link.name,
                params: {
                  [config.link.key]: paramValue,
                },
              },
            },
            () => info.getValue()
          );
        };
      case FieldType.LINK_EXTERNAL:
        return (info: CellContext<any, any>) => {
          return h(
            'a',
            {
              class:
                'tw-underline tw-text-secondary-500 hover:tw-text-primary-600 tw-cursor-pointer',
              href: config?.href,
            },
            () => info.getValue()
          );
        };
    }
  };

  return {
    GetFieldRenderer,
  };
};
